import React, { useEffect, useState, useMemo } from "react";
import Slider from "react-slick";
import styled from "styled-components";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import tw from "twin.macro";

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV;

const Title = tw.h2`
    text-2xl sm:text-4xl font-black tracking-wide text-center mb-16
`;

const Heading = tw.h2`
    text-lg sm:text-xl font-black mb-2 mt-2 ml-2
`;

const NextButton = (props) => {
    const { className, onClick } = props;
    return <NextButtonArrow className={`${className} next`} onClick={onClick} />
};

const PrevButton = (props) => {
    const { className, onClick } = props;
    return <PrevButtonArrow className={`${className} prev`} onClick={onClick} />
};


const NextButtonArrow = styled.div`
  position: absolute;
  top: 50%;
  right: -60px;
  transform: translateY(-50%);
  z-index: 10;
  cursor: pointer;
  font-size: 20px; /* Match with PrevButtonArrow */
  color: white;
  background: #0049AC;
  padding: 10px;
  width: 30px; /* Ensure circular shape */
  height: 30px; /* Same as width */
  border-radius: 50%;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  transition: 0.3s;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #007bff;
  }

  @media (max-width: 768px) {
    right: -20px; /* Adjust button position */
  }

`;


const PrevButtonArrow = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  left: -60px;
  cursor: pointer;
  font-size: 20px; /* Increased for better visibility */
  color: white;
  background: #0049AC;
  padding: 10px;
  width: 30px; /* Ensure it maintains a circular shape */
  height: 30px; /* Same as width */
  border-radius: 50%;
  box-shadow: 0px 2px 5px rgba(0,0,0,0.2);
  transition: 0.3s;
  
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #007bff;
  }

  @media (max-width: 768px) {
    left: -20px; /* Adjust button position */
  }

`;


const StyledSlider = styled(Slider)`
  .slick-list {
    margin: 0 -5px; /* Reduced padding for better fit on smaller screens */
  }

  .slick-slide {
    padding: 0 5px;
  }
`;


const OfferCard = () => {
    const [offers, setOffers] = useState([]);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [whatsappNumber, setWhatsappNumber] = useState("6395977935");

    useEffect(() => {
        const fetchOffers = async () => {
            try {
                const response = await fetch(`${API_URL}/web/home/active-offers`, {
                    headers: {
                        'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
                    }
                });
                const result = await response.json();
                if (result.header.resCode === 200 && result.data.length > 0) {
                    setOffers(result.data);
                }
            } catch (error) {
                console.error("Error fetching offers:", error);
            }
        };

        const fetchWhatsapp = async () => {
            try {

                const response = await fetch(`${API_URL}/web/home/wpnumber?title=whatsappNumber`, {
                    headers: {
                        'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
                    }
                }); // API endpoint updated
                const result = await response.json();

                if (result.header.resCode === 200 && result.data.length > 0) {
                    const numberData = result.data.find((item) => item.title === "whatsappNumber"); // Find the correct video by title
                    if (numberData) {
                        setWhatsappNumber(numberData.content);
                    }
                }

            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchWhatsapp();
        fetchOffers();
    }, []);

    const openModal = (offer) => {
        document.body.style.overflow = 'hidden';
        setSelectedOffer(offer);
    };

    const closeModal = () => {
        document.body.style.overflow = 'auto';
        setSelectedOffer(null);
    };

    const formatTerms = (description) => {
        return description?.split(",").map((term, index) => `${index + 1}. ${term.trim()}`);
    };

    const isExpired = (startDate, endDate) => {
        const now = new Date();
        return new Date(endDate) < now ? "Expired" : "Active";
    };

    const handleMakeEnquiry = (offerData) => {
        const message = `Hello, I am interested in the "${offerData.title}" Offer. Could you provide more details?`;
        const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
        window.open(url, "_blank");
    };

    const slidesToShow = useMemo(() => (offers.length > 2 ? 3 : 1), [offers]);

    const sliderSettings = {
        dots: false,
        infinite: true,
        speed: 200,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        nextArrow: <NextButton />,
        prevArrow: <PrevButton />,
        responsive: [
            {
                breakpoint: 1024, // Tablets
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 768, // Mobile landscape
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480, // Small screens
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    return (
        <>
            {offers.length > 0 && (
                <SliderWrapper>
                    <br /><br /><br /><br />
                    <Title>Exciting Offers @ Traviator Holidays</Title>
                    <StyledSlider {...sliderSettings}>
                        {offers.map((offer) => (
                            <Slide key={offer.id}>
                                <OfferImage
                                    src={offer.banner}
                                    alt={offer.title}
                                    onClick={() => openModal(offer)}
                                />
                                <OfferName>{offer.title}</OfferName>
                                {offer.discount_percentage != 0 && <DiscountTag>{parseInt(offer.discount_percentage)}% OFF</DiscountTag>}
                                <ContactButtonHome onClick={() => handleMakeEnquiry(offer)}>
                                    Contact Now
                                </ContactButtonHome>
                            </Slide>
                        ))}
                    </StyledSlider>
                </SliderWrapper>
            )}

            {selectedOffer && (

                <Modal>
                    <ModalContent>
                        <CloseButton onClick={closeModal} />
                        <Heading>Grab Your Offer @ Traviator</Heading>
                        {/* This wrapper makes only ItineraryCard scrollable */}
                        <ScrollableContent>
                            <ModalImage src={selectedOffer.banner} alt={selectedOffer.title} />
                            <br /><h1 style={{ fontSize: "20px" }}><strong>{selectedOffer.title}</strong></h1>

                            <br /><p><strong>Start Date:</strong> {new Date(selectedOffer.start_date).toLocaleString("en-GB", { day: "2-digit", month: "long", year: "numeric" }).replace(',', '')}</p>
                            <p><strong>End Date:</strong> {new Date(selectedOffer.end_date).toLocaleString("en-GB", { day: "2-digit", month: "long", year: "numeric" }).replace(',', '')}</p>
                            {selectedOffer.discount && <p><strong>Discount:</strong> {parseInt(selectedOffer.discount)}%</p>}
                            <p><strong>Status:</strong> {isExpired(selectedOffer.start_date, selectedOffer.end_date)}</p>
                            <br /><h3><strong>Terms and Conditions:</strong></h3><br />
                            <ul>
                                {formatTerms(selectedOffer.description).map((term, index) => (
                                    <li key={index}>{term}</li>
                                ))}
                            </ul>
                        </ScrollableContent>
                        <ContactButton onClick={() => handleMakeEnquiry(selectedOffer)}>
                            Contact Now
                        </ContactButton>
                    </ModalContent>
                </Modal>

            )}
        </>
    );
};

export default OfferCard;

// Styled Components
const SliderWrapper = styled.div`
  width: 90%;
  margin: auto;
`;


const Slide = styled.div`
  position: relative;
  text-align: center;
`;

const OfferImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
`;

const OfferName = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 5px;
`;

const DiscountTag = styled.div`
  position: absolute;
  top: 200px;
  right: 10px;
  background: green;
  color: white;
  padding: 5px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 5px;
`;

const ContactButton = styled.a`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  background: #0049AC;
  color: white;
  text-align: center;
  padding: 10px;
  border-radius: 5px;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
`;

const ContactButtonHome = styled.a`
  display: block;
  background: #0049AC;
  color: white;
  text-align: center;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
  text-decoration: none;
  cursor: pointer;
  font-weight: bold;
`;

const CloseButton = ({ onClick }) => (
    <button
        onClick={onClick}
        style={{
            position: "absolute",
            top: "2%",  // Adjust as needed
            right: "4%", // Position at the end of the modal
            border: "none",
            fontSize: "30px",
            cursor: "pointer",
            zIndex: "50",
            color: "#333",
            background: "transparent",
        }}
        aria-label="Close"
    >
        &times;
    </button>
);

const Modal = tw.div`fixed z-20 inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center`;

const ModalContent = styled.div`
    position: relative;
    width: 40%;
    background: white;
    padding: 5px;
    border-radius: 8px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    height: 90%; /* Restrict height */
    padding-bottom: 60px;

    @media (max-width: 1068px) {
      width: 90%;
    }

    @media (min-width: 768px) and (max-width: 1068px) {
      width: 60%;
    }
  `;

const ScrollableContent = styled.div`
    overflow-y: auto;
    max-height: 100vh; /* Allow scrolling within modal */
    padding: 10px;
  `;

const ModalImage = styled.img`
  width: 100%;
  height: 200px;
  border-radius: 10px;
`;

